<template>
  <div class="vector-viz-pro">
    <div class="viz-header-compact" >
       <div class="brief" v-latex="knowledgePoint"></div>
       <div class="formula-chip" v-latex>${{ activeFormula }}$</div>
    </div>

    <div class="split-layout">
      <!-- 仿真区域 -->
      <div class="simulation-stage dark-stage flex-1" style="flex: 1.2;">
        <svg width="100%" height="380" viewBox="0 0 800 600" preserveAspectRatio="xMidYMid meet">
           <defs>
              <marker id="arr-blue" viewBox="0 0 10 10" refX="8" refY="5" markerWidth="7" markerHeight="7" orient="auto">
                 <path d="M 0 0 L 10 5 L 0 10 z" fill="#38bdf8" />
              </marker>
              <marker id="arr-green" viewBox="0 0 10 10" refX="8" refY="5" markerWidth="7" markerHeight="7" orient="auto">
                 <path d="M 0 0 L 10 5 L 0 10 z" fill="#34d399" />
              </marker>
              <marker id="arr-red" viewBox="0 0 10 10" refX="8" refY="5" markerWidth="7" markerHeight="7" orient="auto">
                 <path d="M 0 0 L 10 5 L 0 10 z" fill="#f43f5e" />
              </marker>
              <marker id="arr-purple" viewBox="0 0 10 10" refX="8" refY="5" markerWidth="7" markerHeight="7" orient="auto">
                 <path d="M 0 0 L 10 5 L 0 10 z" fill="#a855f7" />
              </marker>
              <filter id="vec-glow"><feGaussianBlur stdDeviation="2" result="blur" /><feComposite in="SourceGraphic" in2="blur" operator="over" /></filter>
           </defs>

           <!-- Grid Background -->
           <g stroke="rgba(241, 245, 249, 0.05)" stroke-width="1.5">
              <line v-for="i in 17" :key="'v'+i" :x1="i*50" y1="0" :x2="i*50" y2="600" />
              <line v-for="i in 13" :key="'h'+i" x1="0" :y1="i*50" x2="800" :y2="i*50" />
           </g>

           <!-- Coordinate System -->
           <line x1="400" y1="0" x2="400" y2="600" stroke="#334155" stroke-width="2" />
           <line x1="0" y1="300" x2="800" y2="300" stroke="#334155" stroke-width="2" />
           <text x="410" y="325" fill="#475569" font-size="20" font-weight="900">O (0,0)</text>

           <g transform="translate(400, 300)">
              <!-- MODE 1: 加减法 (Addition/Subtraction) -->
              <g v-if="mode === 'add_sub'">
                 <!-- 平行四边形法则辅助线 -->
                 <polygon :points="`0,0 ${ax},${-ay} ${ax+bx},${-(ay+by)} ${bx},${-by}`" fill="rgba(56, 189, 248, 0.05)" stroke="rgba(255,255,255,0.2)" stroke-width="2" stroke-dasharray="6,4" />
                 
                 <line 0 y1="0" :x2="ax" :y2="-ay" stroke="#38bdf8" stroke-width="4" marker-end="url(#arr-blue)" filter="url(#vec-glow)"/>
                 <text :x="ax/2 + 10" :y="-ay/2 - 10" fill="#38bdf8" font-size="22" font-weight="900">a</text>

                 <line 0 y1="0" :x2="bx" :y2="-by" stroke="#34d399" stroke-width="4" marker-end="url(#arr-green)" filter="url(#vec-glow)"/>
                 <text :x="bx/2 - 25" :y="-by/2 + 20" fill="#34d399" font-size="22" font-weight="900">b</text>

                 <!-- 结果向量 c = a + b -->
                 <line 0 y1="0" :x2="ax+bx" :y2="-(ay+by)" stroke="#f43f5e" stroke-width="5" marker-end="url(#arr-red)" filter="url(#vec-glow)"/>
                 <text :x="(ax+bx)/2 + 15" :y="-(ay+by)/2" fill="#f43f5e" font-size="24" font-weight="900">c</text>
                 
                 <!-- a - b (连接两端点) -->
                 <line :x1="bx" :y1="-by" :x2="ax" :y2="-ay" stroke="#a855f7" stroke-width="3" marker-end="url(#arr-purple)" stroke-dasharray="8,4" />
                 <text :x="(ax+bx)/2" :y="-(ay+by)/2 - 30" fill="#ba86f8" font-size="18">a - b</text>
              </g>

              <!-- MODE 2: 数量积/点积 (Dot Product) -->
              <g v-if="mode === 'dot'">
                 <!-- a的投影在b上 -->
                 <!-- Dot projection length: (a . b) / |b| -->
                 <line 0 y1="0" :x2="bx" :y2="-by" stroke="#34d399" stroke-width="4" marker-end="url(#arr-green)" />
                 <text :x="bx + 15" :y="-by + 5" fill="#34d399" font-size="22" font-weight="900">b</text>

                 <line 0 y1="0" :x2="ax" :y2="-ay" stroke="#38bdf8" stroke-width="4" marker-end="url(#arr-blue)" />
                 <text :x="ax + 10" :y="-ay - 10" fill="#38bdf8" font-size="22" font-weight="900">a</text>
                 
                 <!-- 投影标记线 -->
                 <line :x1="ax" :y1="-ay" :x2="projX" :y2="-projY" stroke="#facc15" stroke-width="2" stroke-dasharray="5,5" />
                 <!-- 投影向量 -->
                 <line 0 y1="0" :x2="projX" :y2="-projY" stroke="#facc15" stroke-width="5" opacity="0.8" marker-end="none"/>
                 <text :x="projX/2" :y="-projY/2 + 25" fill="#facc15" font-size="18" font-weight="900">a 在 b 的投影</text>
              </g>

              <!-- MODE 3: 向量分解与基底 (Basis/Decomp) -->
              <g v-if="mode === 'decomp'">
                 <!-- 基向量 e1, e2 -->
                 <line 0 y1="0" :x2="e1x" :y2="-e1y" stroke="#34d399" stroke-width="3" marker-end="url(#arr-green)" />
                 <text :x="e1x + 10" :y="-e1y" fill="#34d399" font-size="18" font-weight="900">e₁</text>
                 <line 0 y1="0" :x2="e2x" :y2="-e2y" stroke="#a855f7" stroke-width="3" marker-end="url(#arr-purple)" />
                 <text :x="e2x + 10" :y="-e2y" fill="#a855f7" font-size="18" font-weight="900">e₂</text>

                 <!-- 目标向量 a -->
                 <line 0 y1="0" :x2="ax" :y2="-ay" stroke="#38bdf8" stroke-width="4" marker-end="url(#arr-blue)" filter="url(#vec-glow)"/>
                 <text :x="ax + 15" :y="-ay - 10" fill="#38bdf8" font-size="22" font-weight="900">a</text>

                 <!-- 分解辅助线 -->
                 <line :x1="ax" :y1="-ay" :x2="lambda1 * e1x" :y2="-(lambda1 * e1y)" stroke="#64748b" stroke-width="2" stroke-dasharray="6,4" />
                 <line :x1="ax" :y1="-ay" :x2="lambda2 * e2x" :y2="-(lambda2 * e2y)" stroke="#64748b" stroke-width="2" stroke-dasharray="6,4" />
                 
                 <circle :cx="lambda1 * e1x" :cy="-(lambda1 * e1y)" r="4" fill="#34d399" />
                 <circle :cx="lambda2 * e2x" :cy="-(lambda2 * e2y)" r="4" fill="#a855f7" />
              </g>
           </g>

        </svg>
      </div>

      <!-- 右侧解析面板 -->
      <div class="simulation-stage logic-panel">
         <div class="logic-header">代数与几何分析</div>
         <div class="flex-column-center" style="flex:1; padding: 15px;">
            
            <div class="eq-box text-center">
               <div class="eq-display text-blue">向量空间坐标系</div>
               <div class="seq-data-list">
                  <div class="data-row"><span>向量 a (x,y)</span><span class="hl text-blue">a = ({{ ax.toFixed(1) }}, {{ ay.toFixed(1) }}) | 模: {{ aMag.toFixed(1) }}</span></div>
                  <div class="data-row" v-if="mode !== 'decomp'"><span>向量 b (x,y)</span><span class="hl text-green">b = ({{ bx.toFixed(1) }}, {{ by.toFixed(1) }}) | 模: {{ bMag.toFixed(1) }}</span></div>
                  
                  <template v-if="mode === 'add_sub'">
                     <div class="data-row" style="background: rgba(244,63,94,0.1)"><span>合向量 c = a + b</span><span class="hl text-red">c = ({{ (ax+bx).toFixed(1) }}, {{ (ay+by).toFixed(1) }})</span></div>
                     <div class="data-row"><span>差向量 a - b</span><span class="hl text-purple">({{ (ax-bx).toFixed(1) }}, {{ (ay-by).toFixed(1) }})</span></div>
                  </template>

                  <template v-if="mode === 'dot'">
                     <div class="data-row"><span>夹角 θ</span><span class="hl">{{ angleBetween.toFixed(1) }}°</span></div>
                     <div class="data-row" style="background: rgba(244,63,94,0.1)"><span>数量积 a · b</span><span class="hl text-red">{{ dotProduct.toFixed(1) }}</span></div>
                  </template>

                  <template v-if="mode === 'decomp'">
                     <div class="data-row"><span>基 e₁ (x, y)</span><span class="hl text-green">e₁ = ({{ e1x }}, {{ e1y }})</span></div>
                     <div class="data-row"><span>基 e₂ (x, y)</span><span class="hl text-purple">e₂ = ({{ e2x }}, {{ e2y }})</span></div>
                     <div class="data-row" style="background: rgba(56,189,248,0.1)">
                        <span>分解定理系数组合</span>
                        <span class="hl text-blue">a = {{ lambda1.toFixed(2) }}·e₁ + {{ lambda2.toFixed(2) }}·e₂</span>
                     </div>
                  </template>
               </div>
            </div>

            <div class="eq-box mt-l">
               <p class="desc-text" style="font-size: 13px;"><strong>核心洞察：</strong><br/><br/>{{ tipDesc }}</p>
            </div>
         </div>
      </div>
    </div>

    <!-- 控制台 -->
    <div class="compact-controls">
      <div class="control-card">
         <div class="control-grid-mini">
            <div class="s-item">
               <label>研究模型切换</label>
               <el-radio-group v-model="mode" size="small">
                 <el-radio-button label="add_sub">向量加减运算</el-radio-button>
                 <el-radio-button label="dot">数量积 (内积)</el-radio-button>
                 <el-radio-button label="decomp">平面基本定理</el-radio-button>
               </el-radio-group>
            </div>
            
            <div class="s-item" style="grid-column: span 2; display: flex; gap: 20px;">
               <div style="flex: 1;">
                  <label>向量 a 的横坐标 (x)</label>
                  <el-slider v-model="ax" :min="-200" :max="200" size="small"></el-slider>
               </div>
               <div style="flex: 1;">
                  <label>向量 a 的纵坐标 (y)</label>
                  <el-slider v-model="ay" :min="-200" :max="200" size="small"></el-slider>
               </div>
            </div>

            <div class="s-item" style="grid-column: span 3; display: flex; gap: 20px;" v-if="mode !== 'decomp'">
               <div style="flex: 1;">
                  <label>向量 b 的横坐标 (x)</label>
                  <el-slider v-model="bx" :min="-200" :max="200" size="small"></el-slider>
               </div>
               <div style="flex: 1;">
                  <label>向量 b 的纵坐标 (y)</label>
                  <el-slider v-model="by" :min="-200" :max="200" size="small"></el-slider>
               </div>
            </div>
         </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'VectorGeometryViz',
  props: { knowledgePoint: { type: String, default: '' } },
  data() {
    return {
      mode: 'add_sub',
      ax: 120,
      ay: 80,
      bx: 50,
      by: 150,
      e1x: 100, e1y: 0,
      e2x: 50, e2y: 100
    }
  },
  mounted() {
    this.detectMode();
  },
  watch: {
    knowledgePoint: {
       handler() { this.detectMode(); },
       immediate: true
    }
  },
  computed: {
    topicIntro() {
       if (this.mode === 'dot') return '向量数量积：几何上的标量投影乘积与代数乘积的统一';
       if (this.mode === 'decomp') return '平面向量基本定理：任意非零共面向量皆可拆分为基本向量的唯一线性组合';
       return '向量加减法：空间位移合成定理与平行四边形法则';
    },
    activeFormula() {
       if (this.mode === 'dot') return 'a · b = |a||b|cosθ = x₁x₂ + y₁y₂';
       if (this.mode === 'decomp') return 'a = λ₁e₁ + λ₂e₂';
       return '合位移 c = a + b = (x₁+x₂, y₁+y₂)';
    },
    aMag() { return Math.sqrt(this.ax*this.ax + this.ay*this.ay); },
    bMag() { return Math.sqrt(this.bx*this.bx + this.by*this.by); },
    dotProduct() { return this.ax*this.bx + this.ay*this.by; },
    angleBetween() {
       if (this.aMag === 0 || this.bMag === 0) return 0;
       const cosTheta = this.dotProduct / (this.aMag * this.bMag);
       // handle floating point precision
       let validCos = cosTheta;
       if(validCos > 1) validCos = 1;
       if(validCos < -1) validCos = -1;
       return Math.acos(validCos) * 180 / Math.PI;
    },
    projX() {
       if (this.bMag === 0) return 0;
       return (this.dotProduct / (this.bMag * this.bMag)) * this.bx;
    },
    projY() {
       if (this.bMag === 0) return 0;
       return (this.dotProduct / (this.bMag * this.bMag)) * this.by;
    },
    // For decomposition mode (solve linear equations):
    // a = lambda1 * e1 + lambda2 * e2
    // ax = l1*e1x + l2*e2x
    // ay = l1*e1y + l2*e2y
    lambda1() {
       const det = this.e1x*this.e2y - this.e2x*this.e1y;
       if (det === 0) return 0;
       return (this.ax*this.e2y - this.ay*this.e2x) / det;
    },
    lambda2() {
       const det = this.e1x*this.e2y - this.e2x*this.e1y;
       if (det === 0) return 0;
       return (this.e1x*this.ay - this.e1y*this.ax) / det;
    },
    tipDesc() {
       if (this.mode === 'dot') return '数量积(点积)是物理学求功 W=F·s 的底层数学骨架。点积的本质是向量 a 朝外力的方向 b 上投影出了多少力量，投影力量与b本身的模数相乘，得到一个只有大小没有方向的标量（标量）。当两向量垂直时，投影为0，点结也绝对为0。';
       if (this.mode === 'decomp') return '平面向量基本定理告诉你，二维世界是极其“简单”的。只要你掌握了两个不共线、不平行的基础向量(e₁和e₂)，那么二维世界里任何其他向量，都可以被它们精确“兑换”或者“拆解”成确定比例(λ₁和λ₂)的拼凑叠加。这是整个线性代数的破局基石！';
       return '向量加法遵循平行四边形法则（或是首尾相接的三角形法则），它深刻地表明了：你横向走几步就算它，纵向走几步就算它，几何上的叠加完全等价于横轴与纵轴坐标各自代数相加的值。';
    }
  },
  methods: {
    detectMode() {
      const kp = this.knowledgePoint || '';
      if (/基本定理|模型|应用/.test(kp)) this.mode = 'decomp';
      else if (/积|点积|数量积|内积/.test(kp)) this.mode = 'dot';
      else this.mode = 'add_sub';
    }
  }
}
</script>

<style scoped lang="scss">
.vector-viz-pro { flex: 1; display: flex; flex-direction: column; gap: 12px; height: 100%; overflow: hidden; }
.viz-header-compact { display: flex; justify-content: space-between; align-items: center; padding: 0 4px; .brief { font-size: 15px; color: #1e293b; font-weight: 700; } .formula-chip { background: #eff6ff; color: #1d4ed8; border: 1px solid #bfdbfe; padding: 8px 18px; border-radius: 6px; font-weight: 900; font-family: -apple-system, sans-serif; font-size: 18px; } }
.split-layout { display: flex; gap: 12px; flex: 1; min-height: 360px; }
.flex-1 { flex: 1; min-width: 0; }
.logic-panel { flex: 0.8; min-width: 0; display: flex; flex-direction: column; background: #0f172a !important; border-radius: 12px; }
.logic-header { background: #1e293b; padding: 12px; color: #38bdf8; font-size: 14px; font-weight: 900; text-align: center; border-bottom: 2px solid #0f172a; border-radius: 12px 12px 0 0; }
.flex-column-center { display: flex; flex-direction: column; justify-content: flex-start; }

.eq-box { width: 100%; border-radius: 8px; overflow: hidden; }
.eq-display { background: rgba(0,0,0,0.4); padding: 10px; font-weight: 900; border-bottom: 1px solid rgba(255,255,255,0.05); }
.seq-data-list { display: flex; flex-direction: column; background: rgba(15, 23, 42, 0.6); }
.data-row { display: flex; justify-content: space-between; align-items: center; padding: 12px 18px; border-bottom: 1px solid rgba(255,255,255,0.02); color: #94a3b8; font-size: 13px; font-weight: bold; }
.hl { color: #f8fafc; font-weight: 900; font-family: monospace; font-size: 14px; }

.text-purple { color: #a855f7; } .text-green { color: #34d399; } .text-blue { color: #38bdf8; } .text-red { color: #f43f5e; }
.desc-text { color: #cbd5e1; font-size: 13px; line-height: 1.6; padding: 18px; background: rgba(56, 189, 248, 0.05); border-radius: 8px; border: 1px solid rgba(56, 189, 248, 0.1); }
.mt-l { margin-top: 25px; }

.simulation-stage { background: #020617; border-radius: 12px; border: 1.5px solid #1e293b; position: relative; overflow: hidden; }

.compact-controls { flex: 0; display: flex; flex-direction: column; gap: 10px; }
.control-card { background: #ffffff; padding: 16px 20px; border-radius: 12px; border: 1px solid #e2e8f0; }
.control-grid-mini { display: grid; grid-template-columns: 1fr 1fr; gap: 15px; .s-item { label { font-size: 13px; color: #475569; font-weight: 900; margin-bottom: 8px; display: block; } } }
</style>
